import React from 'react';
import { merge } from 'lodash';
import PreferencesToggle from './PreferenceToggle';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import { MemberPreferences } from '../../../types/business';

export const getDefaultMemberPreferences = (value = false): MemberPreferences => ({
  MEMBERS: {
    CREATE: value,
    // REMOVE: value, // Admin only, not used currently
  },
  EVENTS: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  STATS_AND_SCORES: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  TASKS: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
  PHOTO_ALBUMS: {
    CREATE: value,
    UPDATE: value,
    DELETE: value,
  },
});

interface MemberPreferenceFormProps {
  initialMemberPreferences?: MemberPreferences;
  onMemberPreferencesChange: (memberPreferences: MemberPreferences) => void;
  disableEventPreference?: boolean;
  disableScoreCardPreference?: boolean;
  disableTaskPreference?: boolean;
  disablePhotoAlbumPreference?: boolean;
}

export default function MemberPreferenceForm({
  initialMemberPreferences,
  onMemberPreferencesChange,
  disableTaskPreference,
  disableEventPreference,
  disableScoreCardPreference,
  disablePhotoAlbumPreference,
}: MemberPreferenceFormProps): JSX.Element {
  const { t } = useBusinessTranslation();

  const [memberPreferences, setMemberPreferences] = React.useState<MemberPreferences>(
    merge(getDefaultMemberPreferences(), initialMemberPreferences) || getDefaultMemberPreferences(),
  );

  const handlePreferenceToggle = (category: keyof MemberPreferences, forceValue?: boolean) => {
    const previousSetting = Object.values(memberPreferences[category]!).includes(true);
    setMemberPreferences((prev) => ({
      ...prev,
      [category]: Object.entries(prev[category]!).reduce(
        (acc, [operation]) => ({ ...acc, [operation]: forceValue ?? !previousSetting }),
        {},
      ),
    }));
  };

  React.useEffect(() => {
    onMemberPreferencesChange(memberPreferences);
  }, [memberPreferences, onMemberPreferencesChange]);

  React.useEffect(() => {
    if (disableEventPreference) handlePreferenceToggle('EVENTS', false);
    if (disableTaskPreference) handlePreferenceToggle('TASKS', false);
    if (disableScoreCardPreference) handlePreferenceToggle('STATS_AND_SCORES', false);
    if (disablePhotoAlbumPreference) handlePreferenceToggle('PHOTO_ALBUMS', false);
  }, [
    disableEventPreference,
    disableTaskPreference,
    disableScoreCardPreference,
    disablePhotoAlbumPreference,
  ]);

  const anySettingIsDisabled =
    disableEventPreference ||
    disableTaskPreference ||
    disableScoreCardPreference ||
    disablePhotoAlbumPreference;

  const categories = Object.keys(getDefaultMemberPreferences());

  const isCategoryDisabled = (category: keyof MemberPreferences) =>
    category === 'EVENTS'
      ? disableEventPreference
      : category === 'STATS_AND_SCORES'
        ? disableScoreCardPreference
        : category === 'TASKS'
          ? disableTaskPreference
          : category === 'PHOTO_ALBUMS'
            ? disablePhotoAlbumPreference
            : false;

  return (
    <div className="space-y-4">
      {Object.entries(memberPreferences)
        .sort(([a], [b]) => categories.indexOf(a) - categories.indexOf(b))
        .map(([category, operations], index) => (
          <PreferencesToggle
            key={index}
            disabled={isCategoryDisabled(category as keyof MemberPreferences)}
            title={t(`page.onboarding.businessChooseSettingsScreen.preferences.${category}.title`)}
            subTitle={t(
              `page.onboarding.businessChooseSettingsScreen.preferences.${category}.description`,
            )}
            checked={Object.values(operations).includes(true)}
            onPress={() => handlePreferenceToggle(category as keyof MemberPreferences)}
          />
        ))}
      {anySettingIsDisabled && (
        <div className="h-4 italic">
          {t('page.onboarding.businessChooseSettingsScreen.unavailableSettings')}
        </div>
      )}
    </div>
  );
}
